import React from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import {
  colors,
  fonts,
  spacing,
  borderRadius,
  shadows
} from "../../../constants";

// Field components
import RegionField from "./fields/RegionField";
import ProjectField from "./fields/ProjectField";
import VisualDescriptionField from "./fields/VisualDescriptionField";
import QueryTypeField from "./fields/QueryTypeField";
import PriorityField from "./fields/PriorityField";
import AssignToField from "./fields/AssignToField";

/**
 * BasicInfoSection Component
 * Accordion section for basic information fields
 */
const BasicInfoSection = ({
  basicInfoExpanded,
  setBasicInfoExpanded,
  formData,
  errors,
  focusedFields,
  isRegionSelectionEnabled,
  projects,
  loadingProjects,
  users,
  loadingUsers,
  handleChange,
  handleRegionChange,
  handleFocus,
  handleBlur
}) => {
  return (
    <Accordion
      expanded={basicInfoExpanded}
      onChange={(event, isExpanded) => setBasicInfoExpanded(isExpanded)}
      sx={{
        borderRadius: borderRadius.md,
        border: `1px solid ${basicInfoExpanded ? colors.primaryDark : "#e5e7eb"}`,
        background: colors.backgroundWhite,
        boxShadow: basicInfoExpanded ? shadows.md : "none",
        overflow: "hidden",
        "&:before": {
          display: "none"
        },
        "&.Mui-expanded": {
          margin: 0,
          boxShadow: shadows.md,
          borderColor: colors.primaryDark,
          borderRadius: borderRadius.md
        },
        "&:hover": {
          borderColor: colors.primaryDark,
          boxShadow: shadows.sm
        },
        transition: "all 0.2s ease-in-out"
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={{ color: colors.primaryDark, fontSize: "1.5rem" }}
          />
        }
        sx={{
          px: spacing.md,
          py: spacing.sm,
          minHeight: "56px",
          backgroundColor: basicInfoExpanded ? "#f8f9fa" : "transparent",
          borderRadius: basicInfoExpanded
            ? `${borderRadius.md} ${borderRadius.md} 0 0`
            : borderRadius.md,
          "& .MuiAccordionSummary-content": {
            margin: 0,
            alignItems: "center",
            "&.Mui-expanded": {
              margin: 0
            }
          },
          "&:hover": {
            backgroundColor: "#f8f9fa"
          }
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: fonts.weightSemiBold,
            fontSize: fonts.fontSizeLg,
            color: colors.primaryDark
          }}
        >
          Basic Information
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: spacing.md,
          pb: spacing.md,
          pt: spacing.sm,
          backgroundColor: colors.backgroundWhite,
          borderTop: "1px solid #e5e7eb",
          borderRadius: `0 0 ${borderRadius.md} ${borderRadius.md}`,
          overflow: "visible"
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
            gap: spacing.sm,
            width: "100%"
          }}
        >
          {/* Row 1: Region | Project Name (spans 2 cols for more width) */}
          <RegionField
            formData={formData}
            errors={errors}
            focusedFields={focusedFields}
            isRegionSelectionEnabled={isRegionSelectionEnabled}
            handleRegionChange={handleRegionChange}
            handleFocus={handleFocus}
            handleBlur={handleBlur}
          />

          <Box
            sx={{
              gridColumn: { xs: "auto", sm: "span 2" },
              minWidth: 0
            }}
          >
            <ProjectField
              formData={formData}
              errors={errors}
              focusedFields={focusedFields}
              projects={projects}
              loadingProjects={loadingProjects}
              isRegionSelectionEnabled={isRegionSelectionEnabled}
              handleChange={handleChange}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
            />
          </Box>

          {/* <VisualDescriptionField
            formData={formData}
            focusedFields={focusedFields}
            handleChange={handleChange}
            handleFocus={handleFocus}
            handleBlur={handleBlur}
          /> */}

          {/* Row 2: Query Type | Priority | Assign To */}
          <QueryTypeField
            formData={formData}
            errors={errors}
            focusedFields={focusedFields}
            handleChange={handleChange}
            handleFocus={handleFocus}
            handleBlur={handleBlur}
          />

          <PriorityField
            formData={formData}
            focusedFields={focusedFields}
            handleChange={handleChange}
            handleFocus={handleFocus}
            handleBlur={handleBlur}
          />

          <AssignToField
            formData={formData}
            errors={errors}
            focusedFields={focusedFields}
            users={users}
            loadingUsers={loadingUsers}
            handleChange={handleChange}
            handleFocus={handleFocus}
            handleBlur={handleBlur}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default BasicInfoSection;
